import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatCurrency } from "../../../utils/formatters";
import { useTheme } from "../../../contexts/ThemeContext";
import { CARD_BASE } from "../../../constants";

const COLORS = [
  "#4F8EF7", // Blue
  "#34C759", // Green
  "#FFD600", // Yellow
  "#FF9500", // Orange
  "#A259FF", // Purple
  "#FF3B30", // Red
];

function getAssetBreakdown(assets) {
  let traditional = 0,
    roth = 0,
    nonQualified = 0,
    property = 0;
  assets.forEach((a) => {
    const n = (a.assetTypeName || "").toLowerCase();
    const val = parseFloat(a.balance || a.approximate_value || 0) || 0;
    const taxQ = a.taxQualification || a.tax_qualification_id || "";
    if (
      a.isPrimaryResidence ||
      n.includes("property") ||
      n.includes("real estate") ||
      n.includes("residence")
    ) {
      property += val;
    } else if (taxQ === 1 || taxQ === "1" || /trad/i.test(n)) {
      traditional += val;
    } else if (taxQ === 2 || taxQ === "2" || /roth|tax[- ]?free/i.test(n)) {
      roth += val;
    } else {
      nonQualified += val;
    }
  });
  return [
    { name: "Tax-Deferred (Traditional)", value: traditional },
    { name: "Tax-Free (Roth)", value: roth },
    { name: "Cash & Equivalents (Non-Qualified)", value: nonQualified },
    { name: "Property (Non-Qualified)", value: property },
  ].filter((x) => x.value > 0);
}

const HouseholdNetWorthCard = ({ assets = [], liabilities = [] }) => {
  const { isDarkMode } = useTheme();

  // Calculations
  const totalAssets = useMemo(
    () =>
      assets.reduce(
        (sum, a) =>
          sum + (parseFloat(a.balance || a.approximate_value || 0) || 0),
        0,
      ),
    [assets],
  );
  const totalLiabilities = useMemo(
    () =>
      liabilities.reduce(
        (sum, l) =>
          sum +
          (parseFloat(l.remaining_balance || l.remainingBalance || 0) || 0),
        0,
      ),
    [liabilities],
  );
  const netWorth = totalAssets - totalLiabilities;
  const debtToAssetRatio = totalAssets > 0 ? totalLiabilities / totalAssets : 0;
  const assetBreakdown = useMemo(() => getAssetBreakdown(assets), [assets]);

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          Net Worth
        </h2>
      </div>

      {/* Pie Chart */}
      <div className="flex flex-col items-center">
        <div className="w-[120px] h-[120px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={assetBreakdown}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={45}
              >
                {assetBreakdown.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: isDarkMode ? '#1F2937' : '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  color: isDarkMode ? '#D1D5DB' : '#1F2937',
                  fontSize: '10px'
                }}
                formatter={(value, name) => {
                  const percent = totalAssets > 0 ? (value / totalAssets) * 100 : 0;
                  return [
                    `${formatCurrency(value)} (${percent.toFixed(1)}%)`,
                    name,
                  ];
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-2 mt-2">
          {assetBreakdown.map((entry, index) => (
            <div key={entry.name} className="flex items-center">
              <div
                className="w-2 h-2 rounded-full mr-1"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <span className={`text-[10px] ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {entry.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            <span className="text-sm mr-1">📉</span>Liabilities
          </div>
          <div className={`text-sm font-semibold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
            {formatCurrency(totalLiabilities)}
          </div>
        </div>
        <div className="text-center">
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            <span className="text-sm mr-1">⚖️</span>Debt Ratio
          </div>
          <div className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {debtToAssetRatio > 0 ? (debtToAssetRatio * 100).toFixed(1) : 0}%
          </div>
        </div>
        <div className="text-center">
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
            <span className="text-sm mr-1">💰</span>Assets
          </div>
          <div className={`text-sm font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
            {formatCurrency(totalAssets)}
          </div>
        </div>
      </div>

      {/* Net Worth Summary */}
      <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
        <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          Net Worth
        </span>
        <span className={`text-sm font-bold ${netWorth < 0 ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-green-400' : 'text-green-600')}`}>
          {formatCurrency(netWorth)}
        </span>
      </div>
    </div>
  );
};

export default HouseholdNetWorthCard;
